/*
  Table sort comparators (§6.4, §6.6).

  Three bugs in the helpers these replace (the three per-table comparators that lived in
  helpers/tables.tsx until task 11) are the reason this module exists, and each one is
  unit-tested:

  1. DIRECTION. All three returned an ASCENDING localeCompare when order === "desc". Numeric columns are correct, string
     columns invert, so the header arrow and aria-sort disagree with the rows. Here
     there is exactly one ascending comparison and desc negates it, so a string column
     and a numeric column cannot diverge.

  2. NULLS. The old null/string path returned 0, which is non-transitive — the result depends on the input order. Empty values are
     partitioned out and appended instead, so they sort last in BOTH directions and the
     rank is never negated.

  3. CARD NUMBERS. `number` is a string, so plain localeCompare orders 1, 10, 100, 2.
     The call needs the locale argument: localeCompare(b, "en", { numeric: true }).
     Passing the options object as the second argument puts it in the LOCALES position,
     where it is silently ignored and the sort stays broken.
*/

export type SortDirection = "asc" | "desc";

export type SortValue = string | number | null | undefined;

export type Comparator<T> = (a: T, b: T) => number;

export interface CompareOptions {
  /* Digit runs inside a string compare numerically: "2" before "10". */
  numeric?: boolean;
}

/*
  Empty means "nothing to order by": null, undefined, or a blank string. releaseDate is
  string | null and a blank cell reads the same to a visitor as a missing one.
  A zero is NOT empty — a grade column with no gradings sorts as the number 0 (§6.4).
*/
export const isEmptySortValue = (value: SortValue): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

/* Ascending only. Direction is applied once, by the comparator, to every column type. */
export const compareSortValues = (
  a: SortValue,
  b: SortValue,
  options: CompareOptions = {}
): number => {
  if (typeof a === "number" && typeof b === "number") {
    if (a === b) {
      return 0;
    }
    return a < b ? -1 : 1;
  }
  /*
    Trimmed, because isEmptySortValue above already trims to decide what counts as empty,
    and because the cell renders trimmed: production's Pokémon catalog carries exactly one
    set named " S&V: Stellar Crystal" with a leading space, which sorted above "15th
    Anniversary Set" in an A-Z column while displaying as if it belonged with the S's.
    Sorting has to order what the visitor can actually see.
  */
  return String(a).trim().localeCompare(String(b).trim(), "en", {
    numeric: options.numeric === true,
    sensitivity: "base",
  });
};

/* The card-number compare, spelled out because the locale argument is load-bearing. */
export const compareCardNumbers = (a: string, b: string): number =>
  a.localeCompare(b, "en", { numeric: true, sensitivity: "base" });

export const createComparator = <T>(
  getValue: (row: T) => SortValue,
  direction: SortDirection,
  options: CompareOptions = {}
): Comparator<T> => (a, b) => {
  const aValue = getValue(a);
  const bValue = getValue(b);
  const aEmpty = isEmptySortValue(aValue);
  const bEmpty = isEmptySortValue(bValue);

  if (aEmpty || bEmpty) {
    if (aEmpty && bEmpty) {
      return 0;
    }
    /* Last in both directions, so this is deliberately outside the negation below. */
    return aEmpty ? 1 : -1;
  }

  const ascending = compareSortValues(aValue, bValue, options);
  return direction === "desc" ? -ascending : ascending;
};

/*
  The function the tables call. Rows with an empty value are partitioned out and
  appended in their original order rather than compared, which is what keeps the
  comparator handed to Array.prototype.sort transitive.
*/
export const sortRows = <T>(
  rows: readonly T[],
  getValue: (row: T) => SortValue,
  direction: SortDirection,
  options: CompareOptions = {}
): T[] => {
  const present: T[] = [];
  const empty: T[] = [];

  rows.forEach((row) => {
    if (isEmptySortValue(getValue(row))) {
      empty.push(row);
    } else {
      present.push(row);
    }
  });

  return present.sort(createComparator(getValue, direction, options)).concat(empty);
};

/* Card-number columns: the same partitioning, with the numeric collation turned on. */
export const sortRowsByCardNumber = <T>(
  rows: readonly T[],
  getValue: (row: T) => SortValue,
  direction: SortDirection
): T[] => sortRows(rows, getValue, direction, { numeric: true });

export interface GradeCount {
  grade: string | number;
  count: number;
}

/*
  A grade column sorts by that grade's count, and a missing count is 0 (§6.4) — not
  empty, so a set with no 9.5s ranks below one with a single 9.5 rather than being
  pushed to the end of the table.
*/
export const gradeCountValue = (
  grade: string,
  finalGradeCounts: GradeCount[] | null | undefined
): number => {
  if (!finalGradeCounts) {
    return 0;
  }
  const found = finalGradeCounts.filter(
    (row) => String(row.grade) === String(grade)
  )[0];
  return found && typeof found.count === "number" ? found.count : 0;
};
